import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import api from "../lib/api";
import { toast } from "sonner";
import { Mail, Key, ShieldCheck, Gamepad2, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("credentials");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (tab === "2fa") {
       toast.info("Autentificarea 2FA va fi disponibilă în curând.");
       return;
    }
    setLoading(true);
    setError("");
    try {
      await login(email, password, rememberMe);
      toast.success("Autentificare reușită, bine ai revenit!");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSSO = async (provider) => {
    try {
      const redirectUri = window.location.origin + "/app/auth/callback";
      const { data } = await api.get(`/auth/${provider}/login?redirect_uri=${encodeURIComponent(redirectUri)}`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4">
      <div className="w-full max-w-lg relative">
        {/* Glow effect behind card */}
        <div className="absolute inset-0 bg-[var(--tf-primary)]/20 blur-[100px] rounded-full pointer-events-none" />
        
        <div className="tf-surface tf-clip-card border border-white/5 relative z-10 overflow-hidden shadow-2xl backdrop-blur-xl">
          {/* Header Area */}
          <div className="p-8 pb-6 border-b border-white/5 bg-black/20 text-center relative overflow-hidden">
             {/* Subtle pattern or texture could go here */}
             <div className="inline-flex items-center justify-center w-12 h-12 rounded bg-[var(--tf-primary)]/10 mb-4 border border-[var(--tf-primary)]/20">
               <ShieldCheck className="w-6 h-6 text-[var(--tf-primary)]" />
             </div>
             <h1 className="tf-heading text-3xl font-bold tracking-tight text-white mb-2 uppercase">Identificare</h1>
             <p className="text-sm text-zinc-400">Introdu credențiale valide pentru a accesa platforma.</p>
          </div>

          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Tab Selector */}
              <div className="flex bg-black/40 p-1 rounded tf-clip-card-sm border border-white/5">
                <button
                  type="button"
                  onClick={() => setTab("credentials")}
                  className={`flex-1 py-2 text-xs font-bold tracking-widest uppercase transition-colors rounded ${tab === "credentials" ? "bg-white/10 text-white" : "text-zinc-500 hover:text-zinc-300"}`}
                >
                  Parolă
                </button>
                <button
                  type="button"
                  onClick={() => setTab("2fa")}
                  className={`flex-1 py-2 text-xs font-bold tracking-widest uppercase transition-colors rounded ${tab === "2fa" ? "bg-white/10 text-white" : "text-zinc-500 hover:text-zinc-300"}`}
                >
                  2FA Code
                </button>
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded flex items-start gap-2">
                   <div className="mt-0.5">⚠</div>
                   <div className="flex-1">{error}</div>
                </div>
              )}

              {tab === "credentials" ? (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider block ml-1">Nume Utilizator sau Email</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-4 w-4 text-zinc-500" />
                      </div>
                      <input
                        type="text"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full bg-black/40 border border-white/10 rounded py-2.5 pl-10 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[var(--tf-primary)]/50 focus:ring-1 focus:ring-[var(--tf-primary)]/50 transition-all"
                        placeholder="BL1NG sau oficial@indungi.ro"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between ml-1">
                      <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider block">Parolă Access</label>
                      <Link to="/forgot-password" className="text-xs text-[var(--tf-primary)] hover:underline opacity-80 hover:opacity-100">Ai uitat parola?</Link>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Key className="h-4 w-4 text-zinc-500" />
                      </div>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="w-full bg-black/40 border border-white/10 rounded py-2.5 pl-10 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[var(--tf-primary)]/50 focus:ring-1 focus:ring-[var(--tf-primary)]/50 transition-all"
                        placeholder="••••••••••••"
                      />
                    </div>
                  </div>

                  <div className="pt-2">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <div className="relative flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="peer sr-only"
                        />
                        <div className="w-5 h-5 bg-black/40 border border-white/20 rounded peer-checked:bg-[var(--tf-primary)] peer-checked:border-[var(--tf-primary)] transition-colors"></div>
                        <div className="absolute text-black opacity-0 peer-checked:opacity-100 transition-opacity">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        </div>
                      </div>
                      <span className="text-sm text-zinc-400 group-hover:text-zinc-300 transition-colors select-none">
                        Ține-mă minte pe acest dispozitiv
                      </span>
                    </label>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider block ml-1">Cod Autentificator (2FA)</label>
                    <div className="relative">
                      <input
                        type="text"
                        disabled
                        className="w-full bg-black/40 border border-white/10 rounded py-2.5 px-4 text-white placeholder:text-zinc-600 focus:outline-none transition-all text-center tracking-widest font-mono text-lg"
                        placeholder="000 000"
                      />
                    </div>
                    <p className="text-[10px] text-zinc-500 text-center mt-2">Funcționalitate indisponibilă momentan.</p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || tab === "2fa"}
                className="w-full bg-[var(--tf-primary)] hover:bg-[var(--tf-primary)]/90 text-black font-bold uppercase tracking-wider py-3 px-4 rounded transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {loading ? "Se procesează..." : "Conectare"}
                {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-white/5">
              <p className="text-[10px] font-bold tracking-widest text-zinc-500 uppercase text-center mb-4">
                Sau conectează-te cu
              </p>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => handleSSO("steam")}
                  className="bg-[#171a21] hover:bg-[#2a475e] text-white text-xs font-bold py-2 px-2 rounded border border-white/5 transition-colors flex items-center justify-center gap-2"
                >
                  <Gamepad2 className="w-4 h-4 opacity-70" />
                  Steam
                </button>
                <button
                  type="button"
                  onClick={() => handleSSO("discord")}
                  className="bg-[#5865F2]/20 hover:bg-[#5865F2] text-[#5865F2] hover:text-white border border-[#5865F2]/30 hover:border-[#5865F2] text-xs font-bold py-2 px-2 rounded transition-all flex items-center justify-center gap-2"
                >
                  Discord
                </button>
                <button
                  type="button"
                  onClick={() => handleSSO("google")}
                  className="bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white text-xs font-bold py-2 px-2 rounded border border-white/10 transition-colors"
                >
                  Google
                </button>
              </div>
            </div>

            <p className="text-sm text-zinc-500 text-center mt-8">
              Nu ai cont încă?{" "}
              <Link to="/register" className="text-[var(--tf-primary)] font-semibold hover:underline">
                Creează unul acum
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
